<template>
    <div
        v-if="!isHiddenPing"
        class="h-fit post-container white-rounded-box mb-5 mr-4 ml-5 sm:ml-0"
        :class="{ 'bg-salesium-light-blue': isConnectionRequest || isOrgRequest }"
    >
        <!-- TODO: conditionally make bottom border rounded if there are no comments -->
        <div
            class="flex justify-between post-content-wrapper h-fit pl-7 pb-3 pt-5 mr-9"
            :class="{ 'border-b-2': false }"
        >
            <div class="">
                <div class="flex items-top mb-2">
                    <UserNameRef
                        :showingDefaultAvatar="showingDefaultAvatar"
                        :user="post.user"
                        />
                    <div>
                        <div class="flex flex-col text-hint">
                            <div class="flex flex-row mb-4">
                                <a v-if="post.user.type === 'pro'"
                                    :href="post.user.storefront_url"
                                    class="flex no-underline"
                                    >{{ post.user.display_name }}</a
                                >
                                <span v-else
                                      class="flex no-underline"
                                      >
                                    {{ post.user.display_name }}
                                </span>
                                <span class="flex ml-2 italic">{{
                                    moment(post.created_at).fromNow()
                                }}</span>
                            </div>
                            <component
                                class="linebreak"
                                :is="renderedContent"
                                v-on:tagClicked="tagClicked"
                            ></component>
                            <div class="flex mt-2" v-if="post.documents">
                                <ul v-for="document in post.documents" :key="document.id">
                                    <FilePreview
                                        v-if="document.thumbnail_uri"
                                        :image="document.thumbnail_uri"
                                    />
                                    <a
                                        :href="document.pivot.full_uri"
                                        target="_blank"
                                        class="text-salesium-blue"
                                        >{{ document.name }}</a
                                    >
                                </ul>
                            </div>
                            <div class="mt-6" v-if="includeComments">
                                <Comment
                                    v-for="(reply, i) in visibleReplies"
                                    :key="i"
                                    :comment="reply"
                                    v-on:tagClicked="tagClicked"
                                />
                                <a
                                    v-if="
                                        post.replies.length >
                                        visibleReplies.length
                                    "
                                    class="no-underline"
                                    title="View comments"
                                    @click.prevent="showMoreComments"
                                >
                                    <span class="mr-2">More</span>
                                    <span
                                        v-for="n in 3"
                                        :key="n"
                                        class="w-2 h-2 rounded-full inline-block bg-salesium-red mr-0.5"
                                    ></span>
                                </a>
                                <div>
                                    <a
                                        v-if="owner"
                                        class="no-underline"
                                        @click.prevent="replyToPing"
                                        >Reply</a
                                    >
                                </div>
                            </div>
                        </div>
                        <div class="flex-col" v-if="isOrgRequest || isOrgInvite">
                            <div
                                class="flex mt-5"
                                v-if="
                                    post.mentions.find(
                                        (item) => item.id == user.id
                                    )
                                "
                            >
                                <button
                                    :disabled="organizationRequestAccepted"
                                    class="storefront-btn-blue ml-0 mr-0"
                                    type="button"
                                    @click="handleOrganizationRequest(post, true)"
                                >
                                    <span
                                        v-if="!organizationRequestSaving"
                                        class="inline-block"
                                    >
                                        <svg-vue
                                            icon="user-plus-solid"
                                            class="fill-white h-4 lg:h-5 inline-block mr-3"
                                        />
                                        Accept
                                    </span>
                                    <svg-vue
                                        v-else
                                        icon="spinner"
                                        class="fill-white animate-spin h-4 lg:h-5 inline-block"
                                    />
                                </button>

                                <button
                                    :disabled="organizationRequestAccepted"
                                    class="ml-9 bg-white border br-10 secondary-btn w-24 lg:text-hint text-sm"
                                    type="button"
                                    @click="handleOrganizationRequest(post, false)"
                                >
                                    <span
                                        v-if="!organizationRequestSaving"
                                        class="inline-block"
                                        >Reject</span
                                    >
                                    <svg-vue
                                        v-else
                                        icon="spinner"
                                        class="stroke-salesium-red fill-salesium-red animate-spin h-4 lg:h-5 inline-block"
                                    />
                                </button>
                            </div>
                            <span v-if="organizationRequestAccepted" class="mt-1"
                                >This organization request was handled.</span
                            >
                        </div>
                        <div class="flex-col" v-else-if="isConnectionRequest">
                            <div
                                class="flex mt-5"
                                v-if="
                                    post.mentions.find(
                                        (item) => item.id == user.id
                                    )
                                "
                            >
                                <button
                                    :disabled="connectionRequestAccepted"
                                    class="storefront-btn-blue ml-0 mr-0"
                                    type="button"
                                    @click="handleAcceptRequest(post.id)"
                                >
                                    <span
                                        v-if="!connectionRequestSaving"
                                        class="inline-block"
                                    >
                                        <svg-vue
                                            icon="user-plus-solid"
                                            class="fill-white h-4 lg:h-5 inline-block mr-3"
                                        />
                                        Accept
                                    </span>
                                    <svg-vue
                                        v-else
                                        icon="spinner"
                                        class="fill-white animate-spin h-4 lg:h-5 inline-block"
                                    />
                                </button>

                                <button
                                    :disabled="connectionRequestAccepted"
                                    class="ml-9 bg-white border br-10 secondary-btn w-24 lg:text-hint text-sm"
                                    type="button"
                                    @click="handleHidePing(post.id)"
                                >
                                    <span
                                        v-if="!connectionRequestSaving"
                                        class="inline-block"
                                        >Hide</span
                                    >
                                    <svg-vue
                                        v-else
                                        icon="spinner"
                                        class="fill-salesium-red animate-spin h-4 lg:h-5 inline-block"
                                    />
                                </button>
                            </div>
                            <span v-if="connectionRequestAccepted" class="mt-1"
                                >This connection request was accepted.</span
                            >
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <button
                    type="button"
                    @click.prevent="togglePinned()"
                    title="Pin Post"
                    v-if="includePin"
                >
                    <svg-vue
                        icon="thumbtack-solid"
                        class="w-6 h-6"
                        :class="{
                            'fill-salesium-red': post.is_pinned,
                            'fill-salesium-grey-100': !post.is_pinned,
                        }"
                        title="Pin"
                    />
                </button>
            </div>
        </div>
        <div
            v-if="showCareScoreSurvey"
            class="carescore-survey flex justify-end py-1 pr-2 border-t-2"
        >
            How caring was the communication from your pro?
            <span class="carescore-stars pl-2 flex">
                <svg-vue
                    @mouseover.native="carescoreHover = 1"
                    @click.native="handleCareScoreClick(1)"
                    icon="star-solid"
                    title="1"
                    class="h-6 hover:cursor-pointer"
                    :class="
                        carescoreHover >= 1
                            ? 'fill-salesium-blue'
                            : 'fill-neutral-200'
                    "
                />
                <svg-vue
                    @mouseover.native="carescoreHover = 2"
                    @click.native="handleCareScoreClick(2)"
                    icon="star-solid"
                    title="2"
                    class="h-6 hover:cursor-pointer"
                    :class="
                        carescoreHover >= 2
                            ? 'fill-salesium-blue'
                            : 'fill-neutral-200'
                    "
                />
                <svg-vue
                    @mouseover.native="carescoreHover = 3"
                    @click.native="handleCareScoreClick(3)"
                    icon="star-solid"
                    title="3"
                    class="h-6 hover:cursor-pointer"
                    :class="
                        carescoreHover >= 3
                            ? 'fill-salesium-blue'
                            : 'fill-neutral-200'
                    "
                />
                <svg-vue
                    @mouseover.native="carescoreHover = 4"
                    @click.native="handleCareScoreClick(4)"
                    icon="star-solid"
                    title="4"
                    class="h-6 hover:cursor-pointer"
                    :class="
                        carescoreHover >= 4
                            ? 'fill-salesium-blue'
                            : 'fill-neutral-200'
                    "
                />
                <svg-vue
                    @mouseover.native="carescoreHover = 5"
                    @click.native="handleCareScoreClick(5)"
                    icon="star-solid"
                    title="5"
                    class="h-6 hover:cursor-pointer"
                    :class="
                        carescoreHover >= 5
                            ? 'fill-salesium-blue'
                            : 'fill-neutral-200'
                    "
                />
            </span>
        </div>
        <div
            v-if="careScoreAnswered"
            class="carescore-survey flex justify-end py-1 pr-2 border-t-2"
        >
            Thank you for your feedback about this pro.
        </div>
    </div>
</template>

<script>
import Comment from "./Comment.vue";
import FilePreview from "./FilePreview.vue";
import UserNameRef from "./UserNameRef.vue";
export default {
    props: {
        includeComments: {
            type: Boolean,
            default: false,
        },
        includePin: {
            type: Boolean,
            default: false,
        },
        post: {
            type: Object,
            required: true,
        },
        isLast: {
            type: Boolean,
            default: false,
        },
        isConnectionRequest: {
            type: Boolean,
            default: false,
        },
        user: {
            type: Object,
            required: true,
        },
        isOrgRequest: {
            type: Boolean,
            default: false,
            required: false,
        },
        isOrgInvite: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    data() {
        return {
            visibleReplies: this.includeComments
                ? this.post.replies.slice(0, 3)
                : [],
            observer: null,
            owner: true,
            showCareScoreSurvey: (!!this.post.isShowingCarescore) && (this.post.user !== this.user),
            carescoreHover: 0,
            careScoreAnswered: false,
            careScore: 0,
            connectionRequestSaving: false,
            connectionRequestAccepted: this.checkIfAccepted(),
            organizationRequestAccepted: this.checkIfAccepted(),
            organizationRequestSaving: false,
            isHiddenPing: this.isConnectionRequest || this.isOrgRequest
                ? this.post.mentions[0].pivot.hidden
                : false,
            connectionRequestSender: this.isConnectionRequest
                ? this.post.user
                : null,
            showingDefaultAvatar: true,
        };
    },
    methods: {
        togglePinned(event) {
            axios
                .put("/api/storefront_posts/" + this.post.id, {
                    is_pinned: !this.post.is_pinned,
                })
                .then((response) => {
                    if (response.status === 200) {
                        window.location.reload();
                    }
                })
                .catch((error) => {
                    alert("something went wrong");
                });
        },
        handleIntersection(event) {
            if (event[0].isIntersecting) {
                this.$emit("reachedEnd");
            }
        },
        showMoreComments() {
            this.visibleReplies = [
                ...this.visibleReplies,
                ...this.post.replies.slice(
                    this.visibleReplies.length,
                    this.visibleReplies.length + 3
                ),
            ];
        },
        replyToPing() {
            this.$emit("openPingModal", this.post.user, this.post.id);
        },
        handleCareScoreClick(score) {
            this.careScore = score;

            //data object to send in request
            const data = JSON.stringify({
                careScore: this.careScore.toFixed(1), //converting to needed decimal value
                postOwner: this.post.user.id,
                user: this.user.id,
            });

            axios
                .post("api/user/submit/carescore", data, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                })
                .then((response) => {
                    this.showCareScoreSurvey = false;
                    this.careScoreAnswered = true;
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        tagClicked(tag) {
            this.$emit("tagClicked", tag);
        },
        handleAcceptRequest(pingID) {
            this.connectionRequestSaving = true;
            axios
                .post("/api/pings/" + pingID + "/acceptConnection")
                .then((response) => {
                    this.connectionRequestSaving = false;
                    this.connectionRequestAccepted = true;
                    // TODO AFTER MVP:
                    // have this not reload page and add the newly connected user to
                    // the connected pros widget on the left sidebar of dashboard
                    window.location.reload();
                })
                .catch((error) => {
                    console.log(error);
                    alert("Something went wrong!");
                });
        },
        async handleHidePing(pingID) {
            this.connectionRequestSaving = true;
            try {
                await axios.post("/api/pings/" + pingID + "/hide");
                this.connectionRequestSaving = false;
                this.isHiddenPing = true;
            } catch (error) {
                console.log(error);
                alert("Something went wrong!");
            }
        },
        checkIfAccepted() {
            if (!this.isConnectionRequest && !this.isOrgRequest) {
                return false;
            }
            let mention = this.post.mentions.find(
                (item) => item.id == this.user.id
            );
            if (mention) {
                if (mention.pivot.ping_type == 2 || mention.pivot.ping_type == 4 || mention.pivot.ping_type == 6) {
                    return true;
                }
            }
            return false;
        },
        handleOrganizationRequest(post, accepted) {
            this.organizationRequestSaving = true;
            if(this.isOrgInvite){
                const [domainLine] = post.content.split('\n').filter(line => line.startsWith('with domain: '));
                const domain = domainLine.split(' ').at(-1);
                axios
                    .post(`/api/organizations/${domain}/inviteResponse`, {
                        accepted: accepted,
                        ping_id: post.id,
                    })
                    .then((response) => {
                        this.organizationRequestSaving = false;
                        this.organizationRequestAccepted = true;
                    })
                    .catch((error) => {
                        console.log(error);
                        alert("Something went wrong!");
                    });

            }else{
                const [domainLine] = post.content.split('\n').filter(line => line.startsWith('Domain: '));
                const [__, domain] = domainLine.split(' ');

                axios
                    .put(`/api/organizations/${domain}`, {
                        accepted,
                        ping_id: post.id,
                    })
                    .then((response) => {
                        this.organizationRequestSaving = false;
                        this.organizationRequestAccepted = true;
                    })
                    .catch((error) => {
                        console.log(error);
                        alert("Something went wrong!");
                    });
            }
        }
    },
    computed: {
        renderedContent() {
            const userRegex = RegExp(
                /\B@{([a-z0-9_-]+)}{([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}/gi
            );

            let contentWithMentions = this.post.content.replaceAll(
                userRegex,
                (match, username, id) => {
                    const user = this.post.mentions.find(
                        (mention) => mention.id === id
                    );
                    if (user) {
                        if (user.type == "pro") {
                            return `<a href="${user.storefront_url}" class="no-underline">@${user.display_name}</a>`;
                        } else {
                            return `<a class="no-underline">@${user.display_name}</a>`;
                        }
                    } else {
                        // TODO: implement fallback for non-existent user
                        return `@${username}`;
                    }
                }
            );

            const tagRegex = RegExp(/\B#([a-z0-9_]+)/gi);

            let contentWithTags = contentWithMentions.replaceAll(
                tagRegex,
                (match) => {
                    return `<a @click="()=>{this.$emit('tagClicked', '${match}')}" class="no-underline inline-flex items-center px-3 py-2 rounded-full font-medium leading-4 bg-gray-200 text-gray-800">${match}</a>`;
                }
            );

            return {
                template: "<div>" + contentWithTags + "</div>",
            };
        },
    },
    mounted() {
        if (this.isLast) {
            this.observer = new IntersectionObserver(this.handleIntersection, {
                root: this.$el.parentElement,
            });
            this.observer.observe(this.$el);
        }
    },
    destroyed() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    components: {
        Comment,
        FilePreview,
        UserNameRef,
    },
    watch: {
        post() {
            if (this.includeComments) {
                this.visibleReplies = this.post.replies.slice(0, 3);
            }
        },
    },
};
</script>

<style scoped>
.post-content-wrapper {
    min-height: 184px;
}
.badgeBlue {
    border-width: 4px;
}
.badgeGreen {
    border-width: 4px;
}
.linebreak {
    white-space: pre-wrap;
}
</style>
